import React from 'react';
import { Lock, Star } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { DifficultyLevel, GameType } from '../../types';

interface LevelSelectProps {
  gameType: GameType;
  onLevelSelect: (level: DifficultyLevel) => void;
  onBack: () => void;
}

const LEVEL_INFO = {
  1: { name: 'Beginner', emoji: '🌟', color: 'from-green-400 to-green-500' },
  2: { name: 'Easy', emoji: '⭐', color: 'from-blue-400 to-blue-500' },
  3: { name: 'Medium', emoji: '🏅', color: 'from-yellow-400 to-yellow-500' },
  4: { name: 'Hard', emoji: '🏆', color: 'from-orange-400 to-orange-500' },
  5: { name: 'Expert', emoji: '👑', color: 'from-red-400 to-red-500' },
};

export function LevelSelect({ gameType, onLevelSelect, onBack }: LevelSelectProps) {
  const { userProgress } = useApp();

  const getLevelStatus = (level: DifficultyLevel) => {
    if (!userProgress) return { unlocked: level === 1, stars: 0, percentage: 0 };

    const progress = userProgress.levelProgress[level];
    return {
      unlocked: progress.unlocked,
      stars: progress.stars,
      percentage: progress.completionPercentage,
    };
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
        <h2 className="text-4xl font-bold text-primary text-center mb-2">
          Choose Your Level
        </h2>
        <p className="text-gray-600 text-center mb-6">
          Select a difficulty level to start playing
        </p>

        <div className="grid md:grid-cols-5 gap-4">
          {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((level) => {
            const info = LEVEL_INFO[level];
            const status = getLevelStatus(level);

            return (
              <button
                key={level}
                onClick={() => status.unlocked && onLevelSelect(level)}
                disabled={!status.unlocked}
                className={`
                  relative p-6 rounded-2xl transition-all duration-300 transform
                  ${
                    status.unlocked
                      ? 'hover:scale-105 cursor-pointer shadow-lg hover:shadow-xl'
                      : 'opacity-50 cursor-not-allowed'
                  }
                  bg-gradient-to-br ${info.color} text-white
                `}
              >
                {/* Lock icon for locked levels */}
                {!status.unlocked && (
                  <div className="absolute top-2 right-2">
                    <Lock size={20} />
                  </div>
                )}

                {/* Level emoji */}
                <div className="text-5xl mb-2">{info.emoji}</div>

                {/* Level name */}
                <div className="font-bold text-lg mb-1">{info.name}</div>
                <div className="text-sm opacity-90">Level {level}</div>

                {/* Stars */}
                {status.unlocked && (
                  <div className="flex justify-center gap-1 mt-2">
                    {[1, 2, 3].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        className={star <= status.stars ? 'fill-white' : 'opacity-50'}
                      />
                    ))}
                  </div>
                )}

                {/* Progress */}
                {status.unlocked && status.percentage > 0 && (
                  <div className="mt-2 text-xs opacity-90">
                    {status.percentage}% Complete
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <button onClick={onBack} className="btn-secondary">
            ← Back to Games
          </button>
        </div>
      </div>

      {/* Level descriptions */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Level Guide</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{LEVEL_INFO[1].emoji}</span>
              <span className="font-bold">{LEVEL_INFO[1].name}</span>
            </div>
            <p className="text-sm text-gray-600">
              Simple 3-4 letter words. Perfect for beginners!
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{LEVEL_INFO[2].emoji}</span>
              <span className="font-bold">{LEVEL_INFO[2].name}</span>
            </div>
            <p className="text-sm text-gray-600">
              Common everyday words. Build your vocabulary!
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{LEVEL_INFO[3].emoji}</span>
              <span className="font-bold">{LEVEL_INFO[3].name}</span>
            </div>
            <p className="text-sm text-gray-600">
              More complex words. Challenge yourself!
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{LEVEL_INFO[4].emoji}</span>
              <span className="font-bold">{LEVEL_INFO[4].name}</span>
            </div>
            <p className="text-sm text-gray-600">
              Advanced vocabulary. For serious learners!
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{LEVEL_INFO[5].emoji}</span>
              <span className="font-bold">{LEVEL_INFO[5].name}</span>
            </div>
            <p className="text-sm text-gray-600">
              Expert-level vocabulary. Only for the best!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
